/**
 * Lighting Fixture Tool - BIM 2025 Grade
 * 
 * Lighting placement with:
 * - Host-based placement (Ceiling, Wall, Floor, Face)
 * - Photometric data support (Lumen, Color Temp, Wattage)
 * - Light source definition (Point, Spot, Linear, Area)
 * - Switching groups
 * - Coefficient of Utilization (CU) calculation
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Lighting Types ====================

export type LightingCategory =
    | 'Recessed'            // Can lights, troffers
    | 'Surface'             // Surface mounted
    | 'Pendant'             // Hanging
    | 'Wall Sconce'         // Wall mounted
    | 'Track Lighting'      // Track systems
    | 'Linear'              // LED strips
    | 'Emergency';          // Exit signs, battery packs

export type LightSourceType =
    | 'Point'               // Omnidirectional (bulb)
    | 'Spot'                // Directional (cone)
    | 'Linear'              // Tube/Strip
    | 'Area'                // Panel/Rectangle
    | 'Photometric Web';    // IES file based

export type PhotometricDistribution =
    | 'Spherical'
    | 'Hemispherical'
    | 'Spot'
    | 'Asymmetric';

// ==================== Photometric Data ====================

export interface PhotometricData {
    lumen: number;              // lm
    colorTemperature: number;   // K (2700K, 3000K, 4000K, etc.)
    efficacy: number;           // lm/W
    wattage: number;            // W
    candela: number;            // cd (max intensity)
    beamAngle?: number;         // degrees (for spots)
    fieldAngle?: number;        // degrees
    distribution: PhotometricDistribution;
    iesFile?: string;           // Path to IES file
}

// ==================== Enhanced Geometry ====================

export interface EnhancedLightingGeometryData {
    // Positioning
    position: [number, number, number];
    rotation: [number, number, number]; // Euler angles

    // Host
    hostId?: string;
    mounting: 'Ceiling' | 'Wall' | 'Floor' | 'Face';
    heightFromLevel: number;    // mm

    // Type info
    lightingCategory: LightingCategory;
    sourceType: LightSourceType;

    // Dimensions
    length?: number;            // mm (for linear)
    width?: number;             // mm (for troffers)
    radius?: number;            // mm (for cans)
    pendantLength?: number;     // mm

    // Photometrics
    photometrics: PhotometricData;

    // Electrical
    connectorId: string;

    // Control
    switchId?: string;          // Controlled by
}

// ==================== Enhanced Lighting Element ====================

export interface EnhancedLightingElement extends Element {
    category: 'Lighting Fixtures';
    geometryData: EnhancedLightingGeometryData;

    // Electrical Logic
    electricalData: {
        load: number;           // VA
        voltage: number;        // V
        powerFactor: number;    // 0.9 typ.
        ballastFactor: number;  // 1.0 typ.
    };
}

// ==================== Tool Options ====================

export interface LightingToolOptions {
    // Type
    category: LightingCategory;
    familyName: string;
    typeName: string;

    // Source
    sourceType: LightSourceType;

    // Photometrics
    lumen: number;
    colorTemp: number;
    wattage: number;

    // Dimensions
    length: number;
    width: number;
    radius: number;

    // Placement
    defaultElevation: number; // mm (Ceiling height)
    autoHost: boolean;
    workPlaneBased: boolean;
}

// ==================== Lighting Fixture Tool Class ====================

export class LightingFixtureTool extends BaseTool {
    id = 'lighting_fixture';
    name = 'Lighting Fixture';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private currentHost: Element | null = null;
    private currentQuat: THREE.Quaternion = new THREE.Quaternion();

    private options: LightingToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): LightingToolOptions {
        return {
            category: 'Recessed',
            familyName: 'Recessed Can',
            typeName: '6" Downlight LED',
            sourceType: 'Spot',
            lumen: 800,
            colorTemp: 3000,
            wattage: 9,
            length: 0,
            width: 0,
            radius: 75,
            defaultElevation: 2700, // 2.7m ceiling default
            autoHost: true,
            workPlaneBased: false
        };
    }

    /**
     * Set options from UI
     */
    setOptions(options: Partial<LightingToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): LightingToolOptions {
        return { ...this.options };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersectionPoint(event);
        if (!point) return;

        this.createFixture(point, this.currentHost, this.currentQuat);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersectionPoint(event);

        if (point) {
            this.currentPoint = point;
            this.updatePreview();
        }
    }

    private getIntersectionPoint(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        if (!this.context) return null;

        if (this.options.autoHost) {
            // Check for Ceilings first (if we had them implemented)
            // For now, check for Generic Models acting as ceilings or floors
            const hit = event.intersections.find(i => {
                const id = this.findId(i.object);
                if (!id) return false;
                const el = this.context!.bimStore.project.elements.get(id);
                // Allow hosting on Walls, Ceilings, Floors, Roofs
                return el && ['Ceilings', 'Floors', 'Roofs', 'Walls'].includes(el.category);
            });

            if (hit) {
                const id = this.findId(hit.object)!;
                const el = this.context.bimStore.project.elements.get(id)!;
                this.currentHost = el;

                // Align to face normal
                const normal = hit.face?.normal || new THREE.Vector3(0, 0, 1);

                // Rotation to match normal
                const up = new THREE.Vector3(0, 0, 1);
                this.currentQuat.setFromUnitVectors(up, normal);

                return hit.point;
            }
        }

        // Fallback to active level plane
        this.currentHost = null;
        this.currentQuat.set(0, 0, 0, 1);

        // If not hosting, float at default elevation
        const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const point = new THREE.Vector3();
        event.ray.intersectPlane(plane, point);

        if (point) {
            const snapped = this.snapToGrid(point);
            snapped.z = this.options.defaultElevation / 1000;
            return snapped;
        }

        return null;
    }

    onPointerUp(): void { }

    private updatePreview() {
        if (!this.context) return;

        if (this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'lighting_fixture',
                position: this.currentPoint,
                rotation: new THREE.Euler().setFromQuaternion(this.currentQuat),
                hostId: this.currentHost?.id,
                lightType: this.options.sourceType,
                radius: this.options.radius / 1000,
                color: '#FFFF00' // Yellow for light
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createFixture(point: THREE.Vector3, host: Element | null, rotation: THREE.Quaternion) {
        if (!this.context) return;

        const euler = new THREE.Euler().setFromQuaternion(rotation);

        const photometrics: PhotometricData = {
            lumen: this.options.lumen,
            colorTemperature: this.options.colorTemp,
            wattage: this.options.wattage,
            efficacy: this.options.lumen / this.options.wattage,
            candela: this.options.lumen / 12.57, // Isotropic approx
            distribution: 'Spherical' // Default
        };

        const geometryData: EnhancedLightingGeometryData = {
            position: point.toArray() as [number, number, number],
            rotation: [euler.x, euler.y, euler.z],
            hostId: host?.id,
            mounting: host?.category === 'Walls' ? 'Wall' : 'Ceiling',
            heightFromLevel: point.z * 1000, // Approximate elevation
            lightingCategory: this.options.category,
            sourceType: this.options.sourceType,
            length: this.options.length > 0 ? this.options.length : undefined,
            width: this.options.width > 0 ? this.options.width : undefined,
            radius: this.options.radius > 0 ? this.options.radius : undefined,
            photometrics,
            connectorId: uuidv4()
        };

        // Determine load classification
        const load = this.options.wattage / 0.9; // VA = W / PF

        const fixture: EnhancedLightingElement = {
            id: uuidv4(),
            category: 'Lighting Fixtures',
            type: `${this.options.familyName}: ${this.options.typeName}`,
            parameters: this.buildParameters(geometryData),
            constraints: this.getDefaultConstraints(host),
            geometryData,
            electricalData: {
                load,
                voltage: 120, // Default 120V
                powerFactor: 0.9,
                ballastFactor: 1.0
            }
        };

        this.context.bimStore.addElement(fixture);
    }

    private buildParameters(geom: EnhancedLightingGeometryData): Record<string, any> {
        return {
            'Elevation': Math.round(geom.heightFromLevel),
            'Host': geom.hostId || '<None>',
            'Luminous Flux': `${geom.photometrics.lumen} lm`,
            'Color Temperature': `${geom.photometrics.colorTemperature} K`,
            'Wattage': `${geom.photometrics.wattage} W`,
            'Apparent Load': `${Math.round(geom.photometrics.wattage / 0.9)} VA`,
            'Mounting': geom.mounting
        };
    }

    private getDefaultConstraints(host: Element | null): Constraint[] {
        const constraints: Constraint[] = [];

        if (host) {
            constraints.push({
                type: 'HOST_CONTAINED',
                targetId: host.id,
                data: {}
            });
        }

        return constraints;
    }
}

// Export types

